import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, IconButton, Card, CardContent, CircularProgress, Alert, Chip } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { usePageTitle } from '../../hooks/usePageTitle';
import { getOrderById, updateOrderStatus, OrderDetailResponse } from '../../api/ordersService';
import { getProduct, ApiProduct } from '../../api/productsService';
import { canViewCustomerDetails } from '../../utils/orderVisibility';
import { formatEventDateTime } from '../../utils/eventDate';
import axios from 'axios';

// Helper function to format currency
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'QAR',
  }).format(amount);
};

// Helper function to get short ID (first part of GUID)
const getShortId = (id: string): string => {
  if (!id) return '';
  const parts = id.split('-');
  return parts[0] || id;
};

const OrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const orderId = id; // Use id from URL params
  const navigate = useNavigate();
  
  const [order, setOrder] = useState<OrderDetailResponse['data'] | null>(null);
  const [product, setProduct] = useState<ApiProduct | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (!orderId) {
      setError('Order ID is required');
      setLoading(false);
      return;
    }

    const fetchOrder = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await getOrderById(orderId);
        
        if (response.success && response.data) {
          setOrder(response.data);
          
          // Fetch product if we have productId from packages
          if (response.data.packages && response.data.packages.length > 0) {
            const productId = response.data.packages[0].productId;
            if (productId) {
              try {
                const productData = await getProduct(productId);
                setProduct(productData);
              } catch (productErr) {
                console.error('Error fetching product:', productErr);
                // Don't fail the whole page if product fetch fails
              }
            }
          }
        } else {
          throw new Error('Failed to load order details');
        }
      } catch (err) {
        console.error('Error fetching order:', err);
        
        if (axios.isAxiosError(err)) {
          if (err.code === 'ERR_NETWORK' || err.code === 'ECONNREFUSED' || err.message.includes('Network Error')) {
            setError('Unable to connect to the server. Please make sure the API server is running on http://localhost:3002');
          } else if (err.response) {
            if (err.response.status === 404) {
              setError('Order not found');
            } else {
              setError(`Server error: ${err.response.status} - ${err.response.statusText}`);
            }
          } else if (err.request) {
            setError('No response from server. Please check if the API is running.');
          } else {
            setError(err.message);
          }
        } else {
          setError(err instanceof Error ? err.message : 'Failed to fetch order details');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId]);

  usePageTitle(order ? `Order #${getShortId(order.orderId)}` : 'Order Details');

  const handleBackClick = () => {
    navigate('/orders');
  };

  const handleAcceptOrder = async () => {
    if (!order || !orderId) return;

    try {
      setAccepting(true);
      await updateOrderStatus(orderId, 'Approved'); // Backend uses 'Approved' status
      
      // Refresh order data
      const response = await getOrderById(orderId);
      if (response.success && response.data) {
        setOrder(response.data);
      }
    } catch (err) {
      console.error('Error accepting order:', err);
      
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 400) {
          setError(err.response.data?.message || 'Invalid status transition. This order cannot be accepted.');
        } else {
          setError('Failed to accept order. Please try again.');
        }
      } else {
        setError('Failed to accept order. Please try again.');
      }
    } finally {
      setAccepting(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <Box
        sx={{
          height: '100vh',
          bgcolor: 'white',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress sx={{ color: '#67314f' }} />
      </Box>
    );
  }

  // Show error state
  if (error && !order) {
    return (
      <Box
        sx={{
          height: '100vh',
          bgcolor: 'white',
          width: '100%',
          px: 10,
          py: 5,
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        <IconButton
          onClick={handleBackClick}
          sx={{
            width: 32,
            height: 32,
            borderRadius: 2,
            bgcolor: 'transparent',
            '&:hover': { bgcolor: 'rgba(103, 49, 79, 0.04)' },
            alignSelf: 'flex-start',
          }}
        >
          <ArrowBackIcon sx={{ color: '#67314f', fontSize: 16 }} />
        </IconButton>
        <Alert
          severity="error"
          sx={{
            borderRadius: '12px',
            fontFamily: 'inherit',
          }}
        >
          {error}
        </Alert>
      </Box>
    );
  }

  if (!order) {
    return null;
  }

  // Format event date and time
  // eventTime is the order's real event start datetime; eventDate is date-only.
  // Prefer the time when present so the vendor sees when the event actually is.
  const eventDate = order.event?.eventTime
    ? formatEventDateTime(order.event.eventTime)
    : order.event?.eventDate
      ? formatEventDateTime(order.event.eventDate)
      : 'Not specified';
  
  // Format event location
  const eventLocation = order.event?.eventAddress 
    ? `${order.event.eventAddress}${order.event.eventZone ? `, ${order.event.eventZone}` : ''}${order.event.eventBuildingNumber ? `, Building ${order.event.eventBuildingNumber}` : ''}`
    : 'Not specified';

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return '#c5cbd3';
      case 'approved':
      case 'confirmed':
        return '#426a53';
      case 'in-progress':
        return '#67314f';
      case 'delivery':
        return '#67314f';
      case 'completed':
        return '#426a53';
      case 'cancelled':
      case 'declined':
        return '#925b64';
      default:
        return '#c5cbd3';
    }
  };

  // Check if order can be accepted (status is Pending)
  const canAccept = order.status === 'Pending';
  // Customer details (name, phone, address, notes) unlock after acceptance
  const customerHidden = !canViewCustomerDetails(order.status);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#FFFFFF',
        width: '100%',
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{
            borderRadius: '12px',
            fontFamily: 'inherit',
          }}
        >
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconButton
            onClick={handleBackClick}
            sx={{
              width: 32,
              height: 32,
              borderRadius: 2,
              bgcolor: 'transparent',
              '&:hover': { bgcolor: 'rgba(103, 49, 79, 0.04)' },
            }}
          >
            <ArrowBackIcon sx={{ color: '#67314f', fontSize: 16 }} />
          </IconButton>
          <Typography
            variant="h1"
            sx={{
              fontSize: 44,
              fontWeight: 'bold',
              color: '#181818',
              lineHeight: 1.15,
            }}
          >
            Order #{getShortId(order.orderId)}
          </Typography>
          <Chip
            label={order.status}
            sx={{
              bgcolor: getStatusColor(order.status),
              color: 'white',
              fontSize: 13,
              fontWeight: 'normal',
              height: 32,
              borderRadius: 1,
            }}
          />
        </Box>
        {canAccept && (
          <Button
            variant="contained"
            onClick={handleAcceptOrder}
            disabled={accepting}
            sx={{
              height: 56,
              px: 4,
              borderRadius: 2,
              bgcolor: '#67314f',
              color: '#FFFFFF',
              fontSize: 16,
              fontWeight: 'bold',
              textTransform: 'none',
              '&:hover': { bgcolor: '#5a2a42' },
              '&:disabled': {
                bgcolor: '#c5cbd3',
                color: '#FFFFFF',
              },
            }}
          >
            {accepting ? 'Accepting...' : 'Accept Order'}
          </Button>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        <Box sx={{ flex: '1 1 45%', display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontSize: 20,
                fontWeight: 'bold',
                color: '#181818',
                lineHeight: 1.25,
                mb: 2,
              }}
            >
              Customer
            </Typography>
            <Card sx={{ boxShadow: 'none', border: '1px solid #f1f4f4' }}>
              <CardContent sx={{ p: 0 }}>
                {customerHidden ? (
                  <Box sx={{ p: 2 }}>
                    <Typography sx={{ fontSize: 14, color: '#93989c', fontStyle: 'italic' }}>
                      Customer details will be visible after accepting the order
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Box sx={{ p: 1, borderBottom: '1px solid #f1f4f4' }}>
                      <Typography sx={{ fontSize: 16, color: '#181818' }}>
                        {order.customer.name || 'Unknown'}
                      </Typography>
                    </Box>
                    <Box sx={{ p: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: 16, color: '#181818' }}>Phone:</Typography>
                        <Typography sx={{ fontSize: 16, color: '#181818' }}>
                          {order.customer.phone || 'Not provided'}
                        </Typography>
                      </Box>
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          </Box>

          {order.event && (
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontSize: 20,
                  fontWeight: 'bold',
                  color: '#181818',
                  lineHeight: 1.25,
                  mb: 2,
                }}
              >
                Event
              </Typography>
              <Card sx={{ boxShadow: 'none', border: '1px solid #f1f4f4' }}>
                <CardContent sx={{ p: 0 }}>
                  <Box sx={{ p: 1, borderBottom: '1px solid #f1f4f4' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontSize: 16, color: '#181818' }}>Date and time:</Typography>
                      <Typography sx={{ fontSize: 16, color: '#181818' }}>{eventDate}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ p: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontSize: 16, color: '#181818' }}>Location</Typography>
                      {customerHidden ? (
                        <Typography sx={{ fontSize: 16, color: '#93989c', fontStyle: 'italic', textAlign: 'right' }}>
                          Visible after accepting the order
                        </Typography>
                      ) : (
                        <Typography sx={{ fontSize: 16, color: '#181818', textAlign: 'right' }}>
                          {eventLocation}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}
        </Box>

        <Box sx={{ flex: '1 1 50%', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography
            variant="h4"
            sx={{
              fontSize: 20,
              fontWeight: 'bold',
              color: '#181818',
              lineHeight: 1.25,
            }}
          >
            Ordered
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Show product name if available */}
            {product && (
              <Card
                sx={{
                  bgcolor: '#f6f8f8',
                  boxShadow: 'none',
                  borderRadius: 2,
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Typography sx={{ fontSize: 16, fontWeight: 'bold', color: '#181818', mb: 0.5 }}>
                    Product
                  </Typography>
                  <Typography sx={{ fontSize: 16, color: '#181818' }}>
                    {product.title || order.productName || 'Product'}
                  </Typography>
                  {product.shortDescription && (
                    <Typography sx={{ fontSize: 14, color: '#93989c', mt: 0.5 }}>
                      {product.shortDescription}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            )}
            {order.packages && order.packages.length > 0 ? (
              order.packages.map((pkg, index) => (
                <Card
                  key={pkg.packageId || index}
                  sx={{
                    bgcolor: '#f6f8f8',
                    boxShadow: 'none',
                    borderRadius: 2,
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Typography sx={{ fontSize: 17, fontWeight: 'bold', color: '#181818' }}>
                            {pkg.title || 'Package'}
                          </Typography>
                        </Box>
                        {pkg.subTitle && (
                          <Typography sx={{ fontSize: 14, color: '#93989c' }}>
                            {pkg.subTitle}
                          </Typography>
                        )}
                        {pkg.selectedOptions && pkg.selectedOptions.length > 0 && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
                            {pkg.selectedOptions.map((option, optIndex) => (
                              <Typography key={optIndex} sx={{ fontSize: 16, color: '#181818' }}>
                                {option.label || option.id}
                              </Typography>
                            ))}
                          </Box>
                        )}
                        {pkg.selectedItems && pkg.selectedItems.length > 0 && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
                            {pkg.selectedItems.map((item, itemIndex) => (
                              <Typography key={itemIndex} sx={{ fontSize: 16, color: '#181818' }}>
                                {item.label || item.itemId} × {item.units}
                              </Typography>
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card
                sx={{
                  bgcolor: '#f6f8f8',
                  boxShadow: 'none',
                  borderRadius: 2,
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Typography sx={{ fontSize: 16, color: '#181818' }}>
                    {order.productName || 'Product'}
                  </Typography>
                </CardContent>
              </Card>
            )}
            {!customerHidden && order.specialRequests && (
              <Card
                sx={{
                  bgcolor: '#f6f8f8',
                  boxShadow: 'none',
                  borderRadius: 2,
                  mt: 1,
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Typography sx={{ fontSize: 13, color: '#181818', mb: 0.5, fontWeight: 'bold' }}>
                    Special requests:
                  </Typography>
                  <Typography sx={{ fontSize: 16, color: '#181818' }}>
                    {order.specialRequests}
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 'bold', color: '#181818' }}>Total:</Typography>
            <Typography sx={{ fontSize: 16, fontWeight: 'bold', color: '#181818' }}>
              {formatCurrency(order.totalAmount)}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default OrderDetail;
